import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DashboardCard, StatusActionButton } from '../../../shared/ui';
import { useAuth } from '../../../shared/contexts/AuthContext';
import {
  ledgerV2Api,
  type LedgerV2Item,
} from '../../../api/ledgerV2Api';
import {
  ExecutorBalancesCard,
  type ExecutorBalancesCardHandle,
} from '../../executorBalances';
import { parseApiDateTimeUtc } from '../../../shared/utils/timeFormatter';
import styles from './MyFinancesCard.module.css';

interface MyFinancesCardProps {
  siteId: number;
}

const PREVIEW_LIMIT = 5;

const moneyOpts: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

function formatRub(n: number): string {
  return `${n.toLocaleString('ru-RU', moneyOpts)} ₽`;
}

function movementTypeLabel(code: string): string {
  switch (code) {
    case 'earning':
      return 'Начисление';
    case 'correction':
      return 'Корректировка';
    case 'payment':
    case 'payment_document':
      return 'Платёж';
    default:
      return code || 'Проводка';
  }
}

function movementDetailLine(row: LedgerV2Item): string {
  const base = movementTypeLabel(row.movementType);
  if ((row.movementType === 'earning' || row.movementType === 'correction') && row.earningTypeName) {
    return `${base} · ${row.earningTypeName}`;
  }
  return base;
}

function formatPreviewDate(iso: string): string {
  const d = parseApiDateTimeUtc(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  if (day.getTime() === today.getTime()) return `Сегодня, ${time}`;
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day.getTime() === yesterday.getTime()) return `Вчера, ${time}`;
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const MyFinancesCard: React.FC<MyFinancesCardProps> = ({ siteId }) => {
  const { user } = useAuth();
  const ledgerRef = useRef<ExecutorBalancesCardHandle>(null);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [balance, setBalance] = useState<number | null>(null);
  const [transactions, setTransactions] = useState<LedgerV2Item[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [hasAccess, setHasAccess] = useState(true);

  const loadPreview = useCallback(async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setLoadError(null);

      const [balances, ledger] = await Promise.all([
        ledgerV2Api.getSiteExecutorBalances(siteId, { userId: user.id }),
        ledgerV2Api.getSiteLedger(siteId, { userId: user.id, limit: PREVIEW_LIMIT }),
      ]);

      const total = balances.reduce((sum, row) => sum + (Number(row.balance) || 0), 0);
      setBalance(total);
      setTransactions(ledger);
      setHasAccess(true);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        setHasAccess(false);
        setBalance(null);
        setTransactions([]);
        setLoadError(null);
      } else {
        console.error('MyFinancesCard load error', err);
        setLoadError('Не удалось загрузить финансы');
        setBalance(null);
        setTransactions([]);
      }
    } finally {
      setLoading(false);
    }
  }, [siteId, user?.id]);

  useEffect(() => {
    if (user?.id) {
      loadPreview();
    }
  }, [user?.id, loadPreview]);

  const balanceClass =
    balance === null
      ? ''
      : balance > 0
        ? styles.balancePositive
        : balance < 0
          ? styles.balanceNegative
          : styles.balanceZero;

  const handleShowAll = () => {
    ledgerRef.current?.openPersonal();
  };

  const renderCollapsedBalance = () => {
    if (!user) {
      return <span className={styles.collapsedBalanceMuted}>Войдите в аккаунт</span>;
    }
    if (!hasAccess) {
      return <span className={styles.collapsedBalanceMuted}>Недоступно</span>;
    }
    if (loadError) {
      return <span className={styles.collapsedBalanceMuted}>Не удалось загрузить</span>;
    }
    if (loading && balance === null) {
      return <span className={styles.collapsedBalanceSkeleton} aria-hidden />;
    }
    return (
      <span className={`${styles.collapsedBalance} ${balanceClass}`}>
        {balance !== null ? formatRub(balance) : '—'}
      </span>
    );
  };

  return (
    <>
      <DashboardCard
        id={`my-finances-card-${siteId}`}
        title="Мои финансы"
        isCollapsible
        isCollapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed((prev) => !prev)}
        collapsedPreviewVariant="finances"
        keepCollapsedPreviewWhenExpanded
        collapsedPreview={
          <div className={styles.collapsedPreview}>
            <span className={styles.collapsedLabel}>Накопленный баланс</span>
            {renderCollapsedBalance()}
          </div>
        }
      >
        {!user ? (
          <div className={styles.messageBlock}>
            <i className="ri-user-line" aria-hidden />
            <span>Войдите в аккаунт, чтобы видеть личные финансы</span>
          </div>
        ) : !hasAccess ? (
          <div className={styles.messageBlock}>
            <i className="ri-shield-keyhole-line" aria-hidden />
            <span>Личные финансы доступны участникам объекта</span>
          </div>
        ) : (
          <>
            <div className={styles.sectionHead}>
              <span className={styles.sectionTitle}>Последние операции</span>
              {transactions.length > 0 && (
                <span className={styles.sectionHint}>до {PREVIEW_LIMIT}</span>
              )}
            </div>

            {loadError && (
              <div className={styles.errorBlock}>
                <span>{loadError}</span>
                <button type="button" className={styles.retryBtn} onClick={loadPreview}>
                  Повторить
                </button>
              </div>
            )}

            {!loadError && loading && transactions.length === 0 && (
              <div className={styles.loadingBlock}>
                <i className="ri-loader-4-line ri-spin" aria-hidden />
                <span>Загрузка…</span>
              </div>
            )}

            {!loadError && !loading && transactions.length === 0 && (
              <div className={styles.emptyBlock}>
                <i className="ri-exchange-line" aria-hidden />
                <span>Операций пока нет</span>
              </div>
            )}

            {transactions.length > 0 && (
              <ul className={styles.txList}>
                {transactions.map((row) => {
                  const amt = row.amount ?? 0;
                  const sign = amt > 0 ? 'plus' : amt < 0 ? 'minus' : 'zero';
                  return (
                    <li key={row.id} className={styles.txItem}>
                      <div className={styles.txAccent} data-sign={sign} aria-hidden />
                      <div className={styles.txBody}>
                        <div className={styles.txTop}>
                          <span className={styles.txTitle}>{movementDetailLine(row)}</span>
                          <span className={styles.txAmount} data-sign={sign}>
                            {formatRub(amt)}
                          </span>
                        </div>
                        <div className={styles.txMeta}>
                          <span>{formatPreviewDate(row.createdAt)}</span>
                          {row.showsBalanceAfter !== false && row.balanceAfter != null && (
                            <>
                              <span className={styles.txDot}>·</span>
                              <span>баланс {formatRub(row.balanceAfter)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className={styles.actions}>
              <StatusActionButton
                variant="secondary"
                fullWidth
                onClick={handleShowAll}
                disabled={!hasAccess || !user}
                icon="ri-arrow-right-line"
              >
                Показать всё
              </StatusActionButton>
            </div>
          </>
        )}
      </DashboardCard>

      <ExecutorBalancesCard
        ref={ledgerRef}
        siteId={siteId}
        listHidden
        personalOnly
        title="Мои финансы"
      />
    </>
  );
};

export default MyFinancesCard;
